import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ScrollText, Search } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Avatar, Cargando, Segmentado, campo, panel } from '../asistencia/ui';
import { Encabezado, Vacio } from './ui';
import { CLASE_TONO, armarElementos, fecha, hora, hoyISO, nombrePuerta, queEvento, quienEvento, tonoEvento } from './comun';

const POR_PAGINA = 50;

const TIPOS = [
    { id: '', label: 'Todo' },
    { id: 'pases', label: 'Pases' },
    { id: 'negados', label: 'Negados' },
    { id: 'cs', label: 'Desde Centro SURA' },
];

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const mesYAnio = (iso) => (iso ? `${MESES[Number(iso.slice(5, 7)) - 1]} de ${iso.slice(0, 4)}` : '');

/**
 * Últimos registros: quién abrió o cerró la santamaría, quién usó los ascensores, a quién
 * le negaron el paso y lo hecho desde Centro SURA. Copiado en Centro SURA: no depende del
 * servidor de GeoVision.
 */
const RegistrosVista = ({ vivo, filtroInicial }) => {
    const { suscribir } = vivo;
    const [desde, setDesde] = useState(hoyISO());
    const [hasta, setHasta] = useState(hoyISO());
    const [texto, setTexto] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [elemento, setElemento] = useState(filtroInicial?.elemento || '');
    const [tipo, setTipo] = useState('');
    const [pagina, setPagina] = useState(1);
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    const [version, setVersion] = useState(0);
    const vigente = useRef({ pagina, hasta });
    vigente.current = { pagina, hasta };

    const elementos = useMemo(() => armarElementos(vivo.estado?.puertas || [], null), [vivo.estado]);
    const puertasDe = (clave) => {
        const el = elementos.find((e) => (e.tipo === 'santamaria' ? 'santamaria' : String(e.puerta.id)) === clave);
        return el ? el.puertas.map((p) => p.id).join(',') : undefined;
    };

    useEffect(() => {
        const t = setTimeout(() => setBusqueda(texto.trim()), 400);
        return () => clearTimeout(t);
    }, [texto]);

    useEffect(() => { setPagina(1); }, [desde, hasta, busqueda, elemento, tipo]);

    // Si llega algo nuevo y se está viendo la primera página de hoy, se vuelve a pedir
    useEffect(() => suscribir(() => {
        if (vigente.current.pagina === 1 && vigente.current.hasta >= hoyISO()) setVersion((v) => v + 1);
    }), [suscribir]);

    useEffect(() => {
        let activo = true;
        setCargando(true);
        axiosInstance.get('/api/puertas/eventos', {
            params: {
                desde, hasta, q: busqueda || undefined, puerta: elemento ? puertasDe(elemento) : undefined,
                tipo: tipo || undefined, pagina, por_pagina: POR_PAGINA,
            },
        }).then(({ data }) => {
            if (!activo) return;
            setDatos(data);
            setError(null);
        }).catch((e) => {
            if (activo) setError(e.response?.data?.error || 'No se pudieron consultar los registros');
        }).finally(() => activo && setCargando(false));
        return () => { activo = false; };
        // puertasDe depende de elementos, que solo cambia si cambian las puertas
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [desde, hasta, busqueda, elemento, tipo, pagina, version, elementos.length]);

    const totalPaginas = datos ? Math.max(1, Math.ceil(datos.total / POR_PAGINA)) : 1;
    const variosDias = desde !== hasta;

    return (
        <div className="space-y-6">
            <Encabezado titulo="Últimos registros">
                Quién abrió o cerró la santamaría, quién usó los ascensores, a quién le negaron el paso y lo hecho desde Centro SURA.
                {datos?.desde_cuando && ` Hay registros desde ${mesYAnio(datos.desde_cuando)}.`}
            </Encabezado>

            <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Desde</span>
                    <input type="date" value={desde} max={hasta} onChange={(e) => e.target.value && setDesde(e.target.value)} className={campo} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Hasta</span>
                    <input type="date" value={hasta} min={desde} onChange={(e) => e.target.value && setHasta(e.target.value)} className={campo} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Dónde</span>
                    <select value={elemento} onChange={(e) => setElemento(e.target.value)} className={`${campo} pr-8`}>
                        <option value="">Todas las puertas</option>
                        {elementos.map((el) => (
                            <option key={el.clave} value={el.tipo === 'santamaria' ? 'santamaria' : String(el.puerta.id)}>{el.nombre}</option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 flex-1 min-w-[12rem] max-w-xs">
                    <span className="text-xs text-slate-500">Persona</span>
                    <span className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Nombre o cédula" className={`${campo} w-full pl-9`} />
                    </span>
                </label>
                <Segmentado opciones={TIPOS} valor={tipo} onCambio={setTipo} etiqueta="Qué mostrar" />
            </div>

            {error && <p className="text-sm text-rose-600">{error}</p>}

            <div className={`${panel} overflow-hidden`}>
                {datos && (
                    <p className="px-5 pt-4 text-sm text-slate-500">
                        <span className="font-medium text-slate-900 tabular-nums">{datos.total.toLocaleString('es-VE')}</span>
                        {datos.total === 1 ? ' registro' : ' registros'}
                    </p>
                )}
                {!datos && cargando ? <Cargando /> : datos && datos.eventos.length === 0 ? (
                    <Vacio icono={ScrollText} titulo="No hay registros con estos filtros">Prueba con otras fechas, otra puerta u otro nombre.</Vacio>
                ) : datos && (
                    <div className={`overflow-x-auto transition-opacity ${cargando ? 'opacity-50' : ''}`}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                                    <th className="pl-5 pr-3 py-3 font-medium whitespace-nowrap">{variosDias ? 'Fecha y hora' : 'Hora'}</th>
                                    <th className="px-3 py-3 font-medium">Quién</th>
                                    <th className="px-3 py-3 font-medium">Dónde</th>
                                    <th className="pl-3 pr-5 py-3 font-medium">Qué pasó</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {datos.eventos.map((ev) => {
                                    const quien = quienEvento(ev);
                                    return (
                                        <tr key={ev.id} className="hover:bg-slate-50/60">
                                            <td className="pl-5 pr-3 py-2.5 whitespace-nowrap tabular-nums text-slate-700">
                                                {variosDias && <span className="text-slate-400 mr-2">{fecha(ev.fecha_hora)}</span>}
                                                {hora(ev.fecha_hora, true)}
                                            </td>
                                            <td className="px-3 py-2">
                                                {quien ? (
                                                    <span className="flex items-center gap-2.5 min-w-[12rem]">
                                                        <Avatar persona={{ nombre: quien, foto: ev.empleado?.foto }} size={28} />
                                                        <span className="min-w-0 leading-tight">
                                                            <span className="block text-slate-900 truncate">{quien}</span>
                                                            {!ev.empleado && <span className="block text-xs text-slate-400">No está en Centro SURA</span>}
                                                        </span>
                                                    </span>
                                                ) : <span className="text-slate-400">Sin persona</span>}
                                            </td>
                                            <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{nombrePuerta(ev.puerta)}</td>
                                            <td className={`pl-3 pr-5 py-2.5 min-w-[11rem] ${CLASE_TONO[tonoEvento(ev)]}`}>{queEvento(ev)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {datos && totalPaginas > 1 && (
                <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
                    <span className="tabular-nums">Página {pagina} de {totalPaginas.toLocaleString('es-VE')}</span>
                    <button
                        type="button" onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina === 1 || cargando} aria-label="Página anterior"
                        className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        type="button" onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas || cargando} aria-label="Página siguiente"
                        className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default RegistrosVista;
